import { useRef, useCallback, memo, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AccordionEntryDetailsDto,
  CopyEntryDetailsDto,
  CopyType,
  DEFAULT_COPY_BLOCK,
} from '@act/shared/data-transfer-objects';
import DraggableCopyBlock from './DraggableCopyBlock';

interface DraggableEntryProps {
  entry: AccordionEntryDetailsDto | CopyEntryDetailsDto;
  index: number;
  sectionIndex: number;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onChange: (
    index: number,
    entry: AccordionEntryDetailsDto | CopyEntryDetailsDto
  ) => void;
  onDelete: (index: number) => void;
  type: 'accordion' | 'copy';
}

interface DragItem {
  index: number;
  sectionIndex: number;
  id: string;
  type: string;
}

const DraggableEntry = memo(
  ({
    entry,
    index,
    sectionIndex,
    onMove,
    onChange,
    onDelete,
    type,
  }: DraggableEntryProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(true);

    // Set up React Hook Form
    const methods = useForm({
      defaultValues: {
        copyBlocks:
          type === 'copy' ? (entry as CopyEntryDetailsDto).copyBlocks : [],
        title: type === 'copy' ? (entry as CopyEntryDetailsDto).title : '',
        copyType:
          type === 'copy'
            ? (entry as CopyEntryDetailsDto).copyType
            : 'paragraph',
        summary:
          type === 'accordion'
            ? (entry as AccordionEntryDetailsDto).summary
            : '',
        details:
          type === 'accordion'
            ? (entry as AccordionEntryDetailsDto).details
            : '',
      },
    });

    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: string | symbol | null }
    >({
      accept: `entry-${sectionIndex}`,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) {
          return;
        }

        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        onMove(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag, _preview] = useDrag({
      type: `entry-${sectionIndex}`,
      item: () => ({
        index,
        sectionIndex,
        id: `entry-${sectionIndex}-${index}`,
        type: 'entry',
        isExpanded,
      }),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const opacity = isDragging ? 0.4 : 1;

    drag(drop(ref));

    const textFieldSx = {
      '& .MuiInputBase-input': {
        color: 'text.primary',
      },
      '& .MuiInputLabel-root': {
        color: 'text.primary',
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
        },
      },
    };

    const handleCopyBlockChange = useCallback(
      (blockIndex: number, updatedBlock: { title?: string; copy: string }) => {
        const copyEntry = entry as CopyEntryDetailsDto;
        const newCopyBlocks = [...copyEntry.copyBlocks];
        newCopyBlocks[blockIndex] = {
          ...newCopyBlocks[blockIndex],
          ...updatedBlock,
        };

        onChange(index, {
          ...copyEntry,
          copyBlocks: newCopyBlocks,
        });
      },
      [entry, index, onChange]
    );

    const handleMoveCopyBlock = useCallback(
      (dragIndex: number, hoverIndex: number) => {
        const copyEntry = entry as CopyEntryDetailsDto;
        const dragBlock = copyEntry.copyBlocks[dragIndex];
        const newBlocks = [...copyEntry.copyBlocks];
        newBlocks.splice(dragIndex, 1);
        newBlocks.splice(hoverIndex, 0, dragBlock);

        onChange(index, {
          ...copyEntry,
          copyBlocks: newBlocks,
        });
      },
      [entry, index, onChange]
    );

    const handleAddCopyBlock = useCallback(() => {
      const copyEntry = entry as CopyEntryDetailsDto;
      onChange(index, {
        ...copyEntry,
        copyBlocks: [...copyEntry.copyBlocks, { ...DEFAULT_COPY_BLOCK }],
      });
    }, [entry, index, onChange]);

    const handleRemoveCopyBlock = useCallback(
      (blockIndex: number) => {
        const copyEntry = entry as CopyEntryDetailsDto;
        if (copyEntry.copyBlocks.length > 1) {
          onChange(index, {
            ...copyEntry,
            copyBlocks: copyEntry.copyBlocks.filter(
              (_, idx) => idx !== blockIndex
            ),
          });
        }
      },
      [entry, index, onChange]
    );

    const getEntryIcon = useCallback(() => {
      if (type === 'accordion') {
        return null;
      }

      if (type === 'copy') {
        const copyEntry = entry as CopyEntryDetailsDto;
        // Default icon for paragraph entries
        let icon = <NotesOutlinedIcon />;

        // Only try to determine icon from entries if they exist
        switch (copyEntry.copyType) {
          case 'paragraph':
            icon = <NotesOutlinedIcon />;
            break;
          case 'ordered-list':
            icon = <FormatListNumberedOutlinedIcon />;
            break;
          case 'unordered-list':
            icon = <FormatListBulletedOutlinedIcon />;
            break;
        }

        return icon;
      }
    }, [entry, type]);

    const getEntryTitle = useCallback(() => {
      const defaultTitle = `Entry ${index + 1}`;

      if (type === 'accordion') {
        return (entry as AccordionEntryDetailsDto).summary || defaultTitle;
      }

      if (type === 'copy') {
        return (entry as CopyEntryDetailsDto).title || defaultTitle;
      }

      return defaultTitle;
    }, [entry, type, index]);

    const entryIcon = getEntryIcon();
    const entryTitle = getEntryTitle();

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          border: '1px solid #ddd',
          borderRadius: 1,
          opacity,
          backgroundColor: 'background.paper',
        }}
        data-handler-id={handlerId}
      >
        <FormProvider {...methods}>
          <Box
            ref={headerRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'move',
            }}
          >
            <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
            {entryIcon}
            <Typography
              variant="subtitle1"
              sx={{ flex: 1, color: 'text.primary' }}
            >
              {entryTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                size="small"
                sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  onDelete(index);
                }}
                size="small"
                sx={{
                  color: 'error.main',
                  opacity: 0.25,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'error.lighter',
                  },
                }}
              >
                <RemoveCircleOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Collapse in={isExpanded}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                paddingTop: '16px',
              }}
            >
              {type === 'accordion' ? (
                <>
                  <TextField
                    label="Summary"
                    value={(entry as AccordionEntryDetailsDto).summary}
                    onChange={e =>
                      onChange(index, { ...entry, summary: e.target.value })
                    }
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Details"
                    value={(entry as AccordionEntryDetailsDto).details}
                    onChange={e =>
                      onChange(index, { ...entry, details: e.target.value })
                    }
                    multiline
                    rows={3}
                    fullWidth
                    sx={textFieldSx}
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="Title"
                    value={(entry as CopyEntryDetailsDto).title}
                    onChange={e =>
                      onChange(index, {
                        ...(entry as CopyEntryDetailsDto),
                        title: e.target.value,
                      })
                    }
                    fullWidth
                    sx={textFieldSx}
                  />
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'text.primary' }}>
                      Copy Type
                    </InputLabel>
                    <Select
                      value={(entry as CopyEntryDetailsDto).copyType}
                      label="Copy Type"
                      onChange={e =>
                        onChange(index, {
                          ...(entry as CopyEntryDetailsDto),
                          copyType: e.target.value as CopyType,
                        })
                      }
                      sx={{
                        color: 'text.primary',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <MenuItem value="paragraph">Paragraph</MenuItem>
                      <MenuItem value="ordered-list">Ordered List</MenuItem>
                      <MenuItem value="unordered-list">Unordered List</MenuItem>
                    </Select>
                  </FormControl>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    {(entry as CopyEntryDetailsDto).copyBlocks.map(
                      (block, blockIndex) => (
                        <DraggableCopyBlock
                          key={`${entry.sortOrder}-${blockIndex}`}
                          copyType={(entry as CopyEntryDetailsDto).copyType}
                          block={block}
                          index={blockIndex}
                          entryIndex={index}
                          canDelete={
                            (entry as CopyEntryDetailsDto).copyBlocks.length > 1
                          }
                          onMove={handleMoveCopyBlock}
                          onChange={handleCopyBlockChange}
                          onDelete={handleRemoveCopyBlock}
                        />
                      )
                    )}
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddCopyBlock}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    Add Copy Block
                  </Button>
                </>
              )}
            </Box>
          </Collapse>
        </FormProvider>
      </Box>
    );
  }
);

export default DraggableEntry;
